/**
 * Icono y etiqueta del tiempo, a partir de lo que dice la API.
 *
 * Antes esto se adivinaba en el front —«si llueve más de 5 mm entonces Lluvia, si la
 * humedad pasa de 80 y la potencia solar baja de 150 entonces Nublado»— con dos copias
 * distintas de la heurística (el widget del dashboard y la pantalla de bienvenida) y
 * umbrales que no coincidían. Podía decir «Soleado» un día de lluvia. OpenWeather ya
 * manda `weather[0]` con su código y su descripción traducida: esto sólo traduce el
 * código a un icono.
 *
 * Códigos: https://openweathermap.org/weather-conditions
 *   2xx tormenta · 3xx llovizna · 5xx lluvia · 6xx nieve · 7xx atmósfera (niebla, polvo)
 *   800 despejado · 80x nubes
 */
import {
  Cloud,
  CloudDrizzle,
  CloudFog,
  CloudLightning,
  CloudRain,
  CloudSnow,
  CloudSun,
  Cloudy,
  Moon,
  Sun,
  Wind,
  type LucideIcon,
} from "lucide-react";
import i18n from "i18next";
import type { WeatherCondition } from "@/lib/weather";

/** El icono de OpenWeather acaba en `d` o `n`; de ahí sale si es de día o de noche. */
export function isNight(condition?: WeatherCondition | null): boolean {
  return !!condition?.icon?.endsWith("n");
}

/**
 * Icono para una condición. Sin condición devuelve la nube neutra: es la señal honesta
 * de «no lo sé», no un sol inventado.
 */
export function weatherIcon(condition?: WeatherCondition | null): LucideIcon {
  const id = condition?.id;
  if (typeof id !== "number") return Cloud;

  if (id >= 200 && id < 300) return CloudLightning;
  if (id >= 300 && id < 400) return CloudDrizzle;
  if (id === 511) return CloudSnow; // lluvia helada
  if (id >= 500 && id < 600) return CloudRain;
  if (id >= 600 && id < 700) return CloudSnow;
  if (id >= 700 && id < 800) {
    // 771 (chubascos de viento) y 781 (tornado) son viento, no niebla.
    if (id === 771 || id === 781) return Wind;
    return CloudFog;
  }
  if (id === 800) return isNight(condition) ? Moon : Sun;
  if (id === 801 || id === 802) return isNight(condition) ? Cloud : CloudSun;
  return Cloudy; // 803, 804
}

/**
 * Texto que se enseña: la MISMA palabra que dicen Tactic y Ulture Go para el mismo cielo
 * (usuario 2026-09-26: «no está puesto el mismo tiempo exactamente igual en Tactic y en
 * Pest»). Antes era la descripción del proveedor tal cual —«cielo claro», «algo de nubes»,
 * «nubes dispersas»—, y Tactic decía «Soleado» o «Parcialmente nublado» para lo mismo.
 * Familias: las de `conditionKind` de Tactic. De noche, «despejado» en vez de «soleado».
 * `fallback` se usa cuando la respuesta no trae condición.
 */
export function weatherLabel(
  condition: WeatherCondition | null | undefined,
  fallback: string,
): string {
  const id = condition?.id;
  if (typeof id !== "number") return fallback;
  const key = (k: string, def: string) => i18n.t(`dashboard.weather.cond.${k}`, def);
  if (id >= 200 && id < 300) return key("storm", "Tormenta");
  if (id >= 300 && id < 400) return key("drizzle", "Llovizna");
  if (id === 511) return key("snow", "Nieve"); // lluvia helada
  if (id === 502 || id === 503 || id === 504 || id === 522) return key("heavyRain", "Lluvia fuerte");
  if (id >= 500 && id < 600) return key("rain", "Lluvia");
  if (id >= 600 && id < 700) return key("snow", "Nieve");
  if (id === 771 || id === 781) return key("storm", "Tormenta");
  if (id >= 700 && id < 800) return key("fog", "Niebla");
  if (id === 800) return isNight(condition) ? key("clear", "Despejado") : key("sunny", "Soleado");
  if (id === 801 || id === 802) return key("partly", "Parcialmente nublado");
  if (id === 803 || id === 804) return key("cloudy", "Nublado");
  const d = condition?.description?.trim();
  return d ? d.charAt(0).toUpperCase() + d.slice(1) : fallback;
}
